import React, { useEffect, useState } from 'react';
import {
  Button,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Row,
  Col,
  Card,
  Statistic,
  message,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  DeleteOutlined,
  LoginOutlined,
  ShopOutlined,
  UserOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';
import type { TablePaginationConfig } from 'antd/es/table';
import dayjs from 'dayjs';
import { useTenants } from '../../hooks';
import { DataTable } from '../../components';
import { PLANS, TENANT_STATUS } from '@erpgold/shared';
import { tenantService } from '../../services';

const { Option } = Select;
const { RangePicker } = DatePicker;

const planColors: Record<string, string> = {
  [PLANS.Lite]: 'default',
  [PLANS.Starter]: 'blue',
  [PLANS.Professional]: 'gold',
  [PLANS.Enterprise]: 'purple',
};

const planLabels: Record<string, string> = {
  [PLANS.Lite]: 'Lite',
  [PLANS.Starter]: 'Starter',
  [PLANS.Professional]: 'Professional',
  [PLANS.Enterprise]: 'Enterprise',
};

// BO can receive 'pending' in addition to shared TENANT_STATUS values.
const statusColors: Record<string, string> = {
  [TENANT_STATUS.ACTIVE]: 'success',
  trial: 'cyan',
  [TENANT_STATUS.GRACE]: 'orange',
  [TENANT_STATUS.SUSPENDED]: 'error',
  [TENANT_STATUS.DELETED]: 'default',
  pending: 'default',
};

const statusLabels: Record<string, string> = {
  [TENANT_STATUS.ACTIVE]: 'Hoạt động',
  trial: 'Dùng thử',
  [TENANT_STATUS.GRACE]: 'Gia hạn',
  [TENANT_STATUS.SUSPENDED]: 'Tạm ngưng',
  [TENANT_STATUS.DELETED]: 'Đã xóa',
  pending: 'Chờ xử lý',
};

interface QuickStats {
  total: number;
  active: number;
  trial: number;
  suspended: number;
}

const getErrorMessage = (error: unknown, fallback: string): string => {
  const maybe = error as { response?: { data?: { message?: string } }; message?: string };
  return maybe?.response?.data?.message || maybe?.message || fallback;
};

export const TenantList: React.FC = () => {
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [filters, setFilters] = useState({
    status: undefined as string | undefined,
    plan_id: undefined as string | undefined,
    created_after: undefined as string | undefined,
    created_before: undefined as string | undefined,
    sort_by: 'created_at' as 'created_at' | 'name' | 'last_active_at',
    sort_dir: 'desc' as 'asc' | 'desc',
    search: '',
  });
  const [quickStats, setQuickStats] = useState<QuickStats>({
    total: 0,
    active: 0,
    trial: 0,
    suspended: 0,
  });

  const {
    tenants,
    loading,
    meta,
    setFilters: setTableFilters,
    refetch,
    createTenant,
    suspendTenant,
    activateTenant,
    extendTrial,
    impersonateTenant,
    deleteTenant,
  } = useTenants();

  const [isExtendModalVisible, setIsExtendModalVisible] = useState(false);
  const [extendDays, setExtendDays] = useState(7);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [selectedTenantName, setSelectedTenantName] = useState('');
  const [isDeleteConfirmVisible, setIsDeleteConfirmVisible] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');

  const fetchQuickStats = async () => {
    try {
      const [totalRes, activeRes, trialRes, suspendedRes] = await Promise.all([
        tenantService.getTenants({ page: 1, limit: 1 }),
        tenantService.getTenants({ page: 1, limit: 1, status: TENANT_STATUS.ACTIVE }),
        tenantService.getTenants({ page: 1, limit: 1, status: TENANT_STATUS.TRIAL }),
        tenantService.getTenants({ page: 1, limit: 1, status: TENANT_STATUS.SUSPENDED }),
      ]);

      setQuickStats({
        total: totalRes.meta.total,
        active: activeRes.meta.total,
        trial: trialRes.meta.total,
        suspended: suspendedRes.meta.total,
      });
    } catch {
      // Keep UI usable even when summary stats fail.
    }
  };

  useEffect(() => {
    fetchQuickStats();
  }, []);

  const handleSearch = (value: string) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setTableFilters({ ...filters, search: value, page: 1 });
  };

  const handleFilterChange = (key: string, value: any) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    setTableFilters({ ...newFilters, page: 1 });
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setTableFilters({ ...filters, page, limit: pageSize });
  };

  const handleDateRangeChange = (
    dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null,
  ) => {
    const createdAfter = dates?.[0] ? dates[0].format('YYYY-MM-DD') : undefined;
    const createdBefore = dates?.[1] ? dates[1].format('YYYY-MM-DD') : undefined;
    const newFilters = {
      ...filters,
      created_after: createdAfter,
      created_before: createdBefore,
    };
    setFilters(newFilters);
    setTableFilters({ ...newFilters, page: 1 });
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _tableFilters: Record<string, FilterValue | null>,
    sorter: SorterResult<any> | SorterResult<any>[],
  ) => {
    const nextPage = pagination.current ?? meta.page;
    const nextPageSize = pagination.pageSize ?? meta.limit;
    const rawSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const sortableField = rawSorter?.field;

    const sortBy =
      sortableField === 'name' || sortableField === 'created_at' || sortableField === 'last_active_at'
        ? sortableField
        : filters.sort_by;
    const sortDir: 'asc' | 'desc' = rawSorter?.order === 'ascend' ? 'asc' : 'desc';

    const newFilters = {
      ...filters,
      sort_by: sortBy,
      sort_dir: sortDir,
    };
    setFilters(newFilters);
    setTableFilters({ ...newFilters, page: nextPage, limit: nextPageSize });
  };

  const handleCreateTenant = async (values: any) => {
    try {
      await createTenant({
        ...values,
        plan: values.plan,
      });
      message.success('Tenant created successfully');
      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error(getErrorMessage(error, 'Failed to create tenant'));
    }
  };

  const handleSuspend = async (id: string, name: string) => {
    Modal.confirm({
      title: 'Suspend Tenant',
      content: `Are you sure you want to suspend "${name}"?`,
      onOk: async () => {
        try {
          await suspendTenant(id, 'Admin suspension');
          message.success('Tenant suspended successfully');
          await fetchQuickStats();
        } catch (error) {
          message.error(getErrorMessage(error, 'Failed to suspend tenant'));
        }
      },
    });
  };

  const handleActivate = async (id: string) => {
    try {
      await activateTenant(id);
      message.success('Tenant activated successfully');
      await fetchQuickStats();
    } catch (error) {
      message.error(getErrorMessage(error, 'Failed to activate tenant'));
    }
  };

  const handleImpersonate = async (id: string) => {
    try {
      const payload = await impersonateTenant(id);
      const hasQuery = payload.redirect_url.includes('?');
      const redirectUrl = `${payload.redirect_url}${hasQuery ? '&' : '?'}impersonation_token=${encodeURIComponent(payload.impersonation_token)}`;
      window.open(redirectUrl, '_blank', 'noopener,noreferrer');
      message.success('Impersonation session opened in a new tab');
    } catch (error) {
      message.error(getErrorMessage(error, 'Failed to impersonate tenant'));
    }
  };

  const handleExtendTrial = (id: string, name: string) => {
    setSelectedTenantId(id);
    setSelectedTenantName(name);
    setExtendDays(7);
    setIsExtendModalVisible(true);
  };

  const handleExtendTrialConfirm = async () => {
    if (!selectedTenantId) return;
    try {
      await extendTrial(selectedTenantId, extendDays);
      message.success('Trial extended successfully');
      setIsExtendModalVisible(false);
      setSelectedTenantId(null);
      setSelectedTenantName('');
      await fetchQuickStats();
    } catch (error) {
      message.error(getErrorMessage(error, 'Failed to extend trial'));
    }
  };

  const handleDeleteClick = (id: string, name: string) => {
    setSelectedTenantId(id);
    setSelectedTenantName(name);
    setDeleteConfirmName('');
    setIsDeleteConfirmVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTenantId || deleteConfirmName !== selectedTenantName) return;
    try {
      await deleteTenant(selectedTenantId);
      message.success('Tenant deleted successfully');
      setIsDeleteConfirmVisible(false);
      setSelectedTenantId(null);
      setSelectedTenantName('');
      setDeleteConfirmName('');
      await fetchQuickStats();
    } catch (error) {
      message.error(getErrorMessage(error, 'Failed to delete tenant'));
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <a onClick={() => navigate(`/tenants/${record.id}`)}>{text}</a>
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>{record.email}</span>
        </Space>
      ),
      sorter: true,
      sortOrder: filters.sort_by === 'name' ? (filters.sort_dir === 'asc' ? 'ascend' : 'descend') : undefined,
    },
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      width: 120,
      render: (plan) => (
        <Tag color={planColors[plan] || 'default'}>{planLabels[plan] || String(plan)}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={statusColors[status] || 'default'}>{statusLabels[status] || String(status)}</Tag>
      ),
    },
    {
      title: 'Stores',
      dataIndex: 'store_count',
      key: 'store_count',
      width: 100,
      render: (count) => (
        <Space>
          <ShopOutlined />
          {count}
        </Space>
      ),
    },
    {
      title: 'Users',
      dataIndex: 'user_count',
      key: 'user_count',
      width: 100,
      render: (count) => (
        <Space>
          <UserOutlined />
          {count}
        </Space>
      ),
    },
    {
      title: 'Last Active',
      dataIndex: 'last_active_at',
      key: 'last_active_at',
      width: 170,
      render: (date, record) => {
        const lastActive = date || record.updated_at;
        return lastActive ? dayjs(lastActive).format('YYYY-MM-DD HH:mm') : 'N/A';
      },
      sorter: true,
      sortOrder:
        filters.sort_by === 'last_active_at'
          ? filters.sort_dir === 'asc'
            ? 'ascend'
            : 'descend'
          : undefined,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date) => dayjs(date).format('YYYY-MM-DD'),
      sorter: true,
      sortOrder:
        filters.sort_by === 'created_at'
          ? filters.sort_dir === 'asc'
            ? 'ascend'
            : 'descend'
          : undefined,
    },
  ];

  const filterContent = (
    <Row gutter={16}>
      <Col span={6}>
        <Select
          placeholder="Filter by status"
          allowClear
          style={{ width: '100%' }}
          value={filters.status}
          onChange={(value) => handleFilterChange('status', value)}
        >
          <Option value={TENANT_STATUS.ACTIVE}>Active</Option>
          <Option value="trial">Trial</Option>
          <Option value={TENANT_STATUS.GRACE}>Grace</Option>
          <Option value={TENANT_STATUS.SUSPENDED}>Suspended</Option>
          <Option value="pending">Pending</Option>
        </Select>
      </Col>
      <Col span={6}>
        <Select
          placeholder="Filter by plan"
          allowClear
          style={{ width: '100%' }}
          value={filters.plan_id}
          onChange={(value) => handleFilterChange('plan_id', value)}
        >
          <Option value={PLANS.Lite}>Lite</Option>
          <Option value={PLANS.Starter}>Starter</Option>
          <Option value={PLANS.Professional}>Professional</Option>
          <Option value={PLANS.Enterprise}>Enterprise</Option>
        </Select>
      </Col>
      <Col span={12}>
        <RangePicker
          style={{ width: '100%' }}
          allowEmpty={[true, true]}
          value={
            filters.created_after || filters.created_before
              ? [
                  filters.created_after ? dayjs(filters.created_after) : null,
                  filters.created_before ? dayjs(filters.created_before) : null,
                ]
              : null
          }
          onChange={(dates) =>
            handleDateRangeChange(
              dates as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null,
            )
          }
        />
      </Col>
    </Row>
  );

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Tenants"
              value={quickStats.total}
              prefix={<ShopOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active"
              value={quickStats.active}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Trial"
              value={quickStats.trial}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Suspended"
              value={quickStats.suspended}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
      </Row>

      <DataTable
        title="Tenant Management"
        data={tenants}
        columns={columns}
        loading={loading}
        rowKey="id"
        searchable
        searchPlaceholder="Search by name or email..."
        onSearch={handleSearch}
        onRefresh={refetch}
        filters={filterContent}
        pagination={{
          current: meta.page,
          pageSize: meta.limit,
          total: meta.total,
          onChange: handlePageChange,
        }}
        onChange={handleTableChange}
        onRowClick={(record) => navigate(`/tenants/${record.id}`)}
        extraActions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsModalVisible(true)}
          >
            Add Tenant
          </Button>
        }
        actionColumn={{
          width: 100,
          fixed: 'right',
          actions: (record) => [
            {
              key: 'view',
              label: 'View Details',
              icon: <EyeOutlined />,
              onClick: () => navigate(`/tenants/${record.id}`),
            },
            ...(record.status === 'trial'
              ? [
                  {
                    key: 'extend-trial',
                    label: 'Extend Trial',
                    icon: <ClockCircleOutlined />,
                    onClick: () => handleExtendTrial(record.id, record.name),
                  },
                ]
              : []),
            ...(record.status === TENANT_STATUS.ACTIVE || record.status === 'trial'
              ? [
                  {
                    key: 'suspend',
                    label: 'Suspend',
                    icon: <PauseCircleOutlined />,
                    danger: true,
                    onClick: () => handleSuspend(record.id, record.name),
                  },
                ]
              : [
                  {
                    key: 'activate',
                    label: 'Activate',
                    icon: <PlayCircleOutlined />,
                    onClick: () => handleActivate(record.id),
                  },
                ]),
            {
              key: 'impersonate',
              label: 'Impersonate',
              icon: <LoginOutlined />,
              onClick: () => handleImpersonate(record.id),
            },
            {
              key: 'delete',
              label: 'Delete',
              icon: <DeleteOutlined />,
              danger: true,
              onClick: () => handleDeleteClick(record.id, record.name),
            },
          ],
        }}
      />

      <Modal
        title="Create New Tenant"
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateTenant}
          initialValues={{ plan: 'starter' }}
        >
          <Form.Item
            name="name"
            label="Company Name"
            rules={[{ required: true, message: 'Please enter company name' }]}
          >
            <Input placeholder="Enter company name" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter valid email' },
            ]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>
          <Form.Item name="phone" label="Phone Number">
            <Input placeholder="Enter phone number" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="plan"
                label="Plan"
                rules={[{ required: true, message: 'Please select plan' }]}
              >
                <Select placeholder="Select plan">
                  <Option value={PLANS.Lite}>Lite</Option>
                  <Option value={PLANS.Starter}>Starter</Option>
                  <Option value={PLANS.Professional}>Professional</Option>
                  <Option value={PLANS.Enterprise}>Enterprise</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="password"
                label="Initial Password"
                rules={[{ required: true, message: 'Please enter password' }]}
              >
                <Input.Password placeholder="Enter initial password" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Extend Trial"
        open={isExtendModalVisible}
        onCancel={() => {
          setIsExtendModalVisible(false);
          setSelectedTenantId(null);
          setSelectedTenantName('');
          setExtendDays(7);
        }}
        onOk={handleExtendTrialConfirm}
        width={400}
      >
        <p>Extend trial for <strong>{selectedTenantName}</strong></p>
        <Form.Item label="Number of days">
          <InputNumber
            min={1}
            max={90}
            value={extendDays}
            onChange={(value) => setExtendDays(value || 7)}
            style={{ width: '100%' }}
          />
        </Form.Item>
      </Modal>

      <Modal
        title="Delete Tenant"
        open={isDeleteConfirmVisible}
        okType="danger"
        okButtonProps={{ disabled: deleteConfirmName !== selectedTenantName }}
        onCancel={() => {
          setIsDeleteConfirmVisible(false);
          setSelectedTenantId(null);
          setSelectedTenantName('');
          setDeleteConfirmName('');
        }}
        onOk={handleDeleteConfirm}
        width={500}
      >
        <Alert
          type="error"
          message="This action cannot be undone"
          description="The tenant will be marked for deletion and permanently removed after 90 days."
          showIcon
          style={{ marginBottom: 16 }}
        />
        <p>
          Type <strong>{selectedTenantName}</strong> to confirm deletion:
        </p>
        <Input
          value={deleteConfirmName}
          onChange={(e) => setDeleteConfirmName(e.target.value)}
          placeholder={selectedTenantName}
        />
      </Modal>
    </div>
  );
};

export default TenantList;
